import React, { useEffect, useRef, useState, useCallback } from 'react';
import { AppLoading, Global404 } from 'ui-components/src';
import { Logger } from '@saviynt/common';
import { Button } from '@saviynt/design-system';
import { hideHostAppElements, hideHostAppElementsCleanup, removeDynamicBodyClasses } from '../utils/hideHostElements';

const SingleSpaRegisterMfe = ({ 
  packageName, 
  routePrefix, 
  containerId, 
  errorContainerId,
  location,
  RenderHeader,
  useFeatureFlag
}) => {
  const { isMfeEnabled, isLoading, isError } = useFeatureFlag();
  const [mfeError, setMfeError] = useState(null);
  const [mfeLoading, setMfeLoading] = useState(false);
  const registeredRef = useRef(false);

  const domElementGetter = useCallback(() => {
    const element = document.getElementById(containerId);
    Logger.info('Getting container element:', containerId, element);
    return element;
  }, [containerId]);

  const errorElementGetter = useCallback(() => document.getElementById(errorContainerId), [errorContainerId]);

  // Render containers immediately when component mounts
  useEffect(() => {
    // This ensures containers exist before single-spa registration
  }, []);

  useEffect(() => {
    /* hide host app elements not needed for Gen2 UI */
    hideHostAppElements();
    removeDynamicBodyClasses();

    return () => {
      hideHostAppElementsCleanup();
      // When ISPM PageRoute unmounts, remove the data-theme attribute
      // but keep the localStorage value intact for when we return
      document.body.removeAttribute('data-theme');
    };
  }, []);

  useEffect(() => {
    if (isMfeEnabled && !registeredRef.current) {
      // Defer registration to the next frame so React finishes rendering
      // the header and container DOM before single-spa attempts to mount.
      // Without this, single-spa can trigger mount mid-render, causing
      // "Attempted to synchronously unmount a root while React was already rendering".
      const rafId = requestAnimationFrame(() => loadSingleSpa());
      return () => cancelAnimationFrame(rafId);
    }

    // Register-once semantics: do not unregister on unmount
    // Single-spa will mount/unmount based on activeWhen; registration persists
  }, [isMfeEnabled, packageName, containerId, errorContainerId]);

  const loadSingleSpa = async () => {
    try {
      Logger.info('Registering MFE if needed:', packageName);
      // webpackIgnore: Required for import map architecture - modules resolved at runtime, not build time
      const { registerApplication, getAppNames, preloadApplication } = await import(/* webpackIgnore: true */ 'single-spa');

      const alreadyRegistered = Array.isArray(getAppNames?.()) && getAppNames().includes(packageName);
      
      if (!alreadyRegistered) {
        registerApplication({
          name: packageName,
          app: () => {
            Logger.info('Loading MFE app:', packageName);
            setMfeLoading(true);
            // webpackIgnore: MFE packages loaded via import map, not webpack bundling
            return import(/* webpackIgnore: true */ packageName)
              .then(module => {
                Logger.info('MFE loaded successfully:', module);
                setMfeError(null);
                setMfeLoading(false);
                return module;
              })
              .catch(error => {
                Logger.error('Failed to load MFE:', packageName, error);
                setMfeError(`Failed to load ${packageName}: ${error.message}`);
                setMfeLoading(false);
                // Return mock module for graceful degradation - prevents app crash while showing error in UI
                return {
                  mount: () => Promise.resolve(),
                  unmount: () => Promise.resolve()
                };
              });
          },
          activeWhen: (location) => location.pathname.startsWith(routePrefix),
          customProps: {
            domElementGetter,
            errorElementGetter
          }
        });
        Logger.info('MFE registered successfully');
      } else {
        Logger.info('MFE already registered:', packageName);
      }

      // Mark as registered for this container instance
      registeredRef.current = true;

      /**
       * Preload the MFE bundle to improve first-time navigation performance.
       *
       * - `preloadApplication` (when available) tells single-spa to fetch & parse the
       *   MFE bundle in the background, but not mount it yet. This makes the first
       *   navigation into the MFE much faster.
       *
       * - If `preloadApplication` is not supported by the current single-spa version,
       *   we fall back to a manual `import()`, which achieves a similar effect by
       *   eagerly loading the bundle once registration is complete.
       *
       * Note:
       * - This block only runs when `isMfeEnabled === true`, so disabled MFEs will not
       *   trigger a download.
       * - This is a performance optimization — the app would still function without it,
       *   but initial route activation would feel slower.
       */
      try {
        if (typeof preloadApplication === 'function') {
          await preloadApplication(packageName);
          Logger.info('Preloaded MFE via single-spa:', packageName);
        } else {
          await import(/* webpackIgnore: true */ packageName);
          Logger.info('Prefetched MFE via dynamic import:', packageName);
        }
      } catch (e) {
        Logger.warn('Prefetch failed for MFE:', packageName, e);
      }

    } catch (error) {
      Logger.error('Failed to load single-spa:', error);
      setMfeError(`Single-spa registration failed: ${error.message}`);
    }
  };

  if (isLoading || mfeLoading) return <AppLoading />;
  if (isError) return <Global404 path={location} />;

  if (mfeError) {
    return (
      <>
        <RenderHeader location={location} />
        <div className="alert alert-danger m-3 mt-6" role="alert">
          <strong>MFE Loading Error:</strong> {mfeError}
          <div className="mt-3">
            <Button onClick={() => window.location.reload()}>Reload</Button>
          </div>
        </div>
      </>
    );
  }

  // Always render containers first, then conditional content
  return (
    <>
      {isMfeEnabled ? (
        <RenderHeader location={location} />
      ) : (
        <Global404 path={location} />
      )}
      <div id={containerId} role="main" aria-label={`${packageName} Application`}></div>
    </>
  );
};

export default SingleSpaRegisterMfe;